import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, CheckCheck, AlertTriangle, FileText, Activity as ActivityIcon,
  AtSign, ChevronRight, Inbox, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { useChainStore } from "../data/chainStore";

const PANEL_STYLE = {
  background: "linear-gradient(180deg, rgba(20,22,46,0.78) 0%, rgba(15,17,38,0.78) 100%)",
  border: "1px solid rgba(99,102,241,0.10)",
  backdropFilter: "blur(14px) saturate(140%)",
  WebkitBackdropFilter: "blur(14px) saturate(140%)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset, 0 14px 30px -22px rgba(0,0,0,0.50)",
};

const _ts = (v) => (typeof v === "number" ? v : v ? Date.parse(v) : 0);
const fmtFull = (ts) => new Date(_ts(ts)).toLocaleString("en-GB", {
  weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
});

const classify = (n) => {
  const t = (n.kind || n.body || "").toLowerCase();
  if (/risk|delay|overdue|missing|blocked|alert/.test(t))
    return { Icon: AlertTriangle, tone: "#FCA5A5", group: "risks", label: "Risk" };
  if (/mention|@/.test(t))
    return { Icon: AtSign, tone: "#C4B5FD", group: "mentions", label: "Mention" };
  if (/document|upload|received|sent|requested/.test(t))
    return { Icon: FileText, tone: "#A5B4FC", group: "system", label: "Document" };
  if (/stage|advanced|memorandum|exchange|completion|searches|enquiries/.test(t))
    return { Icon: ActivityIcon, tone: "#34D399", group: "milestones", label: "Milestone" };
  return { Icon: Bell, tone: "#A5B4FC", group: "system", label: "Update" };
};

const FILTERS = [
  { id: "all",      label: "All" },
  { id: "unread",   label: "Unread" },
  { id: "mentions", label: "Mentions" },
  { id: "risks",    label: "Risks" },
  { id: "milestones", label: "Milestones" },
];

const groupKey = (ts) => {
  const now = new Date();
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  const todayStart = start.getTime();
  const yesterdayStart = todayStart - 86_400_000;
  const weekStart = todayStart - 86_400_000 * 7;
  if (ts >= todayStart) return "Today";
  if (ts >= yesterdayStart) return "Yesterday";
  if (ts >= weekStart) return "This week";
  return "Earlier";
};

export default function NotificationsPage() {
  const { notifications, deals, findProperty, markNotifRead, markAllNotifRead } = useChainStore();
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  const enriched = useMemo(() => {
    return notifications.map((n) => {
      const meta = classify(n);
      const deal = n.dealId ? deals.find((d) => d.id === n.dealId) : null;
      const property = deal ? findProperty(deal.propertyId) : null;
      return { ...n, meta, deal, property };
    });
  }, [notifications, deals, findProperty]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (filter === "unread") list = list.filter((n) => !n.read);
    else if (filter !== "all") list = list.filter((n) => n.meta.group === filter);
    return list.slice().sort((a, b) => b.at - a.at);
  }, [enriched, filter]);

  const unread = enriched.filter((n) => !n.read).length;

  const groups = useMemo(() => {
    const m = new Map();
    for (const n of filtered) {
      const k = groupKey(n.at);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(n);
    }
    return Array.from(m.entries());
  }, [filtered]);

  const handleClick = async (n) => {
    if (!n.read) await markNotifRead(n.id);
    if (n.deal) nav(`/deal-rooms/${n.deal.id}`);
  };

  const handleMarkAll = async () => {
    if (unread === 0) return;
    await markAllNotifRead();
    toast.success("All notifications marked as read");
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: "#0A0D1A", color: "#fff" }}
      data-testid="notifications-page"
    >
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen relative">
        <span aria-hidden="true" className="absolute -top-32 right-1/4 w-[800px] h-[400px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(ellipse, rgba(99,102,241,0.10), transparent 70%)" }} />

        <div className="relative px-4 sm:px-6 lg:px-10 pt-7 pb-12 max-w-4xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6 pl-12 md:pl-0 md:pr-48">
            <div>
              <h1 className="font-display text-[28px] sm:text-[34px] font-bold tracking-[-0.025em] text-white leading-none">
                Notifications
              </h1>
              <p className="text-[12.5px] text-slate-400 mt-2">
                {unread === 0 ? "You're all caught up." : `${unread} unread · operational alerts and mentions across your transactions.`}
              </p>
            </div>
            <button
              type="button"
              onClick={handleMarkAll}
              disabled={unread === 0}
              className="h-9 px-3.5 rounded-lg text-[11.5px] font-bold inline-flex items-center gap-1.5 transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{
                background: unread > 0
                  ? "linear-gradient(135deg, rgba(139,92,246,0.32) 0%, rgba(99,102,241,0.20) 100%)"
                  : "rgba(255,255,255,0.04)",
                border: unread > 0 ? "1px solid rgba(139,92,246,0.36)" : "1px solid rgba(255,255,255,0.08)",
                color: unread > 0 ? "#fff" : "#94a3b8",
              }}
              data-testid="notifications-mark-all"
            >
              <CheckCheck size={12} /> Mark all read
            </button>
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-2 flex-wrap mb-5">
            <Filter size={12} className="text-slate-500" />
            {FILTERS.map((f) => {
              const active = filter === f.id;
              const count = f.id === "unread"
                ? unread
                : f.id === "all"
                  ? enriched.length
                  : enriched.filter((n) => n.meta.group === f.id).length;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  data-testid={`notifications-filter-${f.id}`}
                  className={`h-8 px-3 rounded-lg text-[11.5px] font-semibold inline-flex items-center gap-1.5 transition-all ${active ? "text-white" : "text-slate-400 hover:text-white"}`}
                  style={active ? {
                    background: "linear-gradient(135deg, rgba(139,92,246,0.32) 0%, rgba(99,102,241,0.20) 100%)",
                    border: "1px solid rgba(139,92,246,0.36)",
                  } : {
                    background: "rgba(255,255,255,0.025)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {f.label}
                  <span className="text-[9.5px] tabular-nums opacity-70">{count}</span>
                </button>
              );
            })}
          </div>

          {/* List */}
          {filtered.length === 0 ? (
            <div className="rounded-2xl p-12 text-center" style={PANEL_STYLE} data-testid="notifications-empty">
              <span
                className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-4"
                style={{
                  background: "rgba(16,185,129,0.14)",
                  border: "1px solid rgba(16,185,129,0.30)",
                  color: "#34D399",
                }}
              >
                <Inbox size={20} />
              </span>
              <p className="font-display text-[16px] font-bold text-white tracking-tight">
                {filter === "unread" ? "No unread notifications" : "Nothing to show"}
              </p>
              <p className="text-[12.5px] text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
                We'll surface operational alerts, mentions and risk events here as your transactions progress.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map(([label, items]) => (
                <section key={label} data-testid={`notifications-group-${label}`}>
                  <h3 className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3 px-1">
                    {label} <span className="ml-2 text-slate-600">· {items.length}</span>
                  </h3>
                  <ul className="space-y-2">
                    {items.map((n) => (
                      <li key={n.id}>
                        <button
                          type="button"
                          onClick={() => handleClick(n)}
                          data-testid={`notifications-item-${n.id}`}
                          className="w-full text-left rounded-2xl p-4 flex items-start gap-3.5 hover:bg-white/[0.02] transition-colors group"
                          style={{
                            ...PANEL_STYLE,
                            border: !n.read
                              ? "1px solid rgba(139,92,246,0.32)"
                              : PANEL_STYLE.border,
                          }}
                        >
                          <span
                            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                            style={{
                              background: `${n.meta.tone}20`,
                              border: `1px solid ${n.meta.tone}38`,
                              color: n.meta.tone,
                            }}
                          >
                            <n.meta.Icon size={13} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: n.meta.tone }}>
                                {n.meta.label}
                              </span>
                              {!n.read && (
                                <span
                                  className="w-1.5 h-1.5 rounded-full shrink-0"
                                  style={{ background: "#8B5CF6", boxShadow: "0 0 6px rgba(139,92,246,0.7)" }}
                                  aria-label="Unread"
                                />
                              )}
                              <span className="text-[10.5px] text-slate-500 tabular-nums ml-auto">
                                {fmtFull(n.at)}
                              </span>
                            </div>
                            <p className={`text-[12.5px] leading-snug ${n.read ? "text-slate-300" : "text-white font-semibold"}`}>
                              {n.body}
                            </p>
                            {n.property && (
                              <p className="text-[10.5px] text-slate-500 mt-1 truncate">
                                {n.property.address}
                              </p>
                            )}
                          </div>
                          {n.deal && (
                            <ChevronRight size={14} className="text-slate-500 group-hover:text-white shrink-0 self-center" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
